/**
 * Facebook Pixel Event Tracking Utility
 * Pixel ID: 3090240137814327
 * 
 * Usage:
 * import { trackFBEvent, FB_EVENTS } from './utils/facebookPixel';
 * 
 * trackFBEvent(FB_EVENTS.COMPLETE_REGISTRATION);
 * trackFBEvent(FB_EVENTS.LEAD, { content_name: 'Newsletter Signup' });
 */

// Standard Facebook Pixel Events
export const FB_EVENTS = {
  // Conversion Events
  COMPLETE_REGISTRATION: 'CompleteRegistration',
  LEAD: 'Lead',
  SUBSCRIBE: 'Subscribe',
  START_TRIAL: 'StartTrial',
  SUBMIT_APPLICATION: 'SubmitApplication',
  
  // E-commerce Events
  ADD_TO_CART: 'AddToCart',
  ADD_TO_WISHLIST: 'AddToWishlist',
  INITIATE_CHECKOUT: 'InitiateCheckout',
  ADD_PAYMENT_INFO: 'AddPaymentInfo',
  PURCHASE: 'Purchase',
  
  // Engagement Events
  CONTACT: 'Contact',
  DONATE: 'Donate',
  FIND_LOCATION: 'FindLocation',
  SCHEDULE: 'Schedule',
  SEARCH: 'Search',
  VIEW_CONTENT: 'ViewContent',
  
  // Custom Events (for Yesod app)
  PRAYER_COMPLETED: 'PrayerCompleted',
  JOINED_PRAYER_GROUP: 'JoinedPrayerGroup',
  SET_REMINDER: 'SetReminder',
  CANDLE_LIGHTING_REMINDER: 'CandleLightingReminder',
  DOWNLOAD_APP_CLICK: 'DownloadAppClick',
  SHARE_ACTION: 'ShareAction',
};

/**
 * Track a Facebook Pixel event
 * @param {string} eventName - Event name from FB_EVENTS
 * @param {Object} parameters - Optional event parameters
 */
export const trackFBEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      if (parameters && Object.keys(parameters).length > 0) {
        window.fbq('track', eventName, parameters);
      } else {
        window.fbq('track', eventName);
      }
      console.log(`[FB Pixel] Tracked event: ${eventName}`, parameters);
    } catch (error) {
      console.error('[FB Pixel] Error tracking event:', error);
    }
  } else {
    console.warn('[FB Pixel] fbq not available');
  }
};

/**
 * Track a custom Facebook Pixel event
 * @param {string} eventName - Custom event name
 * @param {Object} parameters - Optional event parameters
 */
export const trackCustomFBEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      window.fbq('trackCustom', eventName, parameters);
      console.log(`[FB Pixel] Tracked custom event: ${eventName}`, parameters);
    } catch (error) {
      console.error('[FB Pixel] Error tracking custom event:', error);
    }
  } else {
    console.warn('[FB Pixel] fbq not available');
  }
};

/**
 * Track page view (already auto-tracked, but can be called manually for SPAs)
 */
export const trackPageView = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      window.fbq('track', 'PageView');
      console.log('[FB Pixel] Tracked PageView');
    } catch (error) {
      console.error('[FB Pixel] Error tracking PageView:', error);
    }
  }
};

// Example usage for common Yesod events:

/**
 * Track user registration/signup
 */
export const trackRegistration = (userData = {}) => {
  trackFBEvent(FB_EVENTS.COMPLETE_REGISTRATION, {
    content_name: 'User Registration',
    status: 'completed',
    ...userData,
  });
};

/**
 * Track candle lighting reminder setup
 */
export const trackCandleLightingReminder = (city, method = 'phone') => {
  trackFBEvent(FB_EVENTS.SET_REMINDER, {
    content_name: 'Candle Lighting Reminder',
    content_category: 'Shabbat',
    city: city,
    method: method, // 'phone' or 'email'
  });
};

/**
 * Track app download clicks
 */
export const trackAppDownload = (platform = 'ios') => {
  trackFBEvent(FB_EVENTS.DOWNLOAD_APP_CLICK, {
    content_name: 'App Download',
    platform: platform, // 'ios' or 'android'
  });
};

/**
 * Track prayer group join
 */
export const trackPrayerGroupJoin = (groupId, groupName) => {
  trackFBEvent(FB_EVENTS.JOINED_PRAYER_GROUP, {
    content_name: 'Prayer Group Join',
    content_id: groupId,
    group_name: groupName,
  });
};

/**
 * Track donations
 */
export const trackDonation = (amount, currency = 'USD', campaign = '') => {
  trackFBEvent(FB_EVENTS.DONATE, {
    value: amount,
    currency: currency,
    content_name: campaign || 'General Donation',
  });
};

/**
 * Track share actions
 */
export const trackShare = (contentType, method) => {
  trackFBEvent(FB_EVENTS.SHARE_ACTION, {
    content_type: contentType, // 'candle_lighting', 'prayer_group', etc.
    share_method: method, // 'whatsapp', 'facebook', 'copy_link'
  });
};

export default {
  trackFBEvent,
  trackCustomFBEvent,
  trackPageView,
  trackRegistration,
  trackCandleLightingReminder,
  trackAppDownload,
  trackPrayerGroupJoin,
  trackDonation,
  trackShare,
  FB_EVENTS,
};
